import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import * as actions from '../../Redux/Actions';
import { Award } from './components/Awards/rnr.types'; 
import MainCard from '../../component/MainCard/MainCard';
import { showErrorToastMessage, showSuccessToastMessage } from '../../component/Toasts/Toasts';
import { Table, Dropdown, Tooltip, OverlayTrigger, Badge, Form} from 'react-bootstrap';
import SearchBar from '../../component/SearchBar/SearchBar';
import FilterValues from '../../component/FieldFilter/FilterValues';
import { RiDeleteBinLine, RiEditLine } from 'react-icons/ri';
import AwardModal from './components/Awards/AwardModal';
import DeleteAwardModal from './components/Awards/DeleteAwardModal';
import "./components/Awards/Awards.scss";

interface Filters {
    [key: string]: string[];
}

const Awards = () => {
    const role = localStorage.getItem('role');
    const isHR = role?.includes('hr') && ( role?.includes('employee-db-hrmanager') || role?.includes('employee-db-hrbp') || role?.includes('hr-ta'));

    const dispatch = useDispatch();
    const [awards, setAwards] = useState<Award[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAwardModal, setShowAwardModal] = useState(false);
    const [editingAward, setEditingAward] = useState<Award | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [awardToDelete, setAwardToDelete] = useState<Award | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState<Filters>({});

    const fetchAwards = () => {
        setLoading(true);
        dispatch(actions.listAwardsAction((res: any) => {
            setLoading(false);
            if (res.success) {
                setAwards(res.data || []);
                showSuccessToastMessage("Awards fetched successfully");
            } else {
                console.error("Failed to fetch awards list:", res.error);
                showErrorToastMessage("Failed to fetch awards list");
            }
        }));
    };

    useEffect(() => {
        fetchAwards();
    }, [dispatch]);

    // const filterOptions = useMemo(() => {
    //     const options: { [key: string]: string[] } = {
    //         frequency: [],
    //         windowStatus: [],
    //     };

    //     awards.forEach((award: any) => {
    //         // compute window info for each award
    //         const window = getNominationWindow(
    //             award.frequency,
    //             award.fiscalYearStartMonth || 1,
    //             dayjs(),
    //             award.nominationWindowDates
    //         );
    //         // collect frequency
    //         if (award.frequency && !options.frequency.includes(award.frequency)) {
    //             options.frequency.push(award.frequency);
    //         }
    //         // collect windowStatus
    //         if (window.status && !options.windowStatus.includes(window.status)) {
    //             options.windowStatus.push(window.status);
    //         }
    //         // collect nominationWindow label
    //         if (window.label && !options.nominationWindow.includes(window.label)) {
    //             options.nominationWindow.push(window.label);
    //         }
    //     });

    //     Object.keys(options).forEach(key => options[key].sort());
    //     return options;
    // }, [awards]);

    const filterOptions = useMemo(() => {
        const options: { [key: string]: string[] } = {
            frequency: [],
            windowStatus: [],
        };

        awards.forEach((award: Award) => { 
            const status = award.nominationWindowStatus?.status;
            const frequency = award.nominationWindow?.frequency;

            if (frequency && !options.frequency.includes(frequency)) {
                options.frequency.push(frequency);
            }
            if (status && !options.windowStatus.includes(status)) {
                options.windowStatus.push(status);
            }
        });

        Object.keys(options).forEach(key => options[key].sort());
        return options;
    }, [awards]);
    
    const getEligibilityText = (eligibility: Award['eligibility']): React.ReactNode => {
        if (!eligibility) {
            return "All employees are eligible.";
        }

        const parts: JSX.Element[] = [];

        if (eligibility.tenure) {
            parts.push(
                <div key="tenure">
                    <strong><i>Minimum Tenure:</i> </strong> {`${eligibility.tenure} months in Skillmine.`}
                </div>
            );
        }
        if (eligibility.specialConditions) {
            parts.push(
                <div key="specialConditions">
                    <strong><i>Conditions:</i> </strong> {eligibility.specialConditions}
                </div>
            );
        }

        if (parts.length === 0) {
            return "All employees are eligible.";
        }
        return parts;
    };

    const processedAwards = useMemo(() => {
        let result = awards;

        // Object.entries(filters).forEach(([field, values]) => {
        //     if (values.length > 0) {
        //         result = result.filter(award => {
        //             if (field === "windowStatus" || field === "nominationWindow") {
        //                 const window = getNominationWindow(
        //                     award.frequency,
        //                     award.fiscalYearStartMonth || 1,
        //                     dayjs(),
        //                 );
        //                 if (field === "windowStatus") {
        //                     return values.includes(window.status);
        //                 }
        //                 if (field === "nominationWindow") {
        //                     return values.includes(window.label);
        //                 }
        //             }
        //             return values.includes((award as any)[field]);
        //         });
        //     }
        // });

        Object.entries(filters).forEach(([field, values]) => {
            if (values.length > 0) {
                result = result.filter(award => {
                    // --- FIX: Filter by the backend's calculated status ---
                    if (field === "windowStatus") {
                        return values.includes(award.nominationWindowStatus?.status || '');
                    }
                    if (field === "frequency") {
                        return values.includes(award.nominationWindow?.frequency);
                    }
                    return values.includes((award as any)[field]);
                });
            }
        });

        //Search filter
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            result = result.filter(award => {
                const name = (award?.awardCategory || '').toLowerCase();
                const eligibilityParts: string[] = [];
                    if (award.eligibility?.tenure) {
                        eligibilityParts.push(`minimum tenure: ${award.eligibility.tenure} months in skillmine.`);
                    }
                    if (award.eligibility?.specialConditions) {
                        eligibilityParts.push(`conditions: ${award.eligibility.specialConditions}`);
                    }
                const eligibilityText = eligibilityParts.length > 0 
                        ? eligibilityParts.join(' ') 
                        : "all employees are eligible.";
                return name.includes(lowerCaseQuery) || eligibilityText.includes(lowerCaseQuery);
            });
        }

        return result;
    }, [awards, searchQuery, filters]);


    const handleFilterChange = (field: string, value: string) => {
        setFilters(prevFilters => {
            const currentValues = prevFilters[field] || [];
            const newValues = currentValues.includes(value)
                ? currentValues.filter(v => v !== value)
                : [...currentValues, value];

            if (newValues.length === 0) {
                const { [field]: _, ...rest } = prevFilters;
                return rest;
            }
            
            return { ...prevFilters, [field]: newValues };
        });
    };

    const removeFilter = (filterToRemove: { field: string, value: string }) => {
        handleFilterChange(filterToRemove.field, filterToRemove.value);
    };

    const activeFiltersForDisplay = Object.entries(filters).flatMap(([field, values]) => 
        values.map(value => ({ title: `${field}: ${value}`, value: value, field: field }))
    );
    

     const handleOpenAddModal = () => {
        setEditingAward(null);
        setShowAwardModal(true);
    };

    const handleOpenEditModal = (award: Award) => {
        setEditingAward(award); 
        setShowAwardModal(true);
    };

    const handleCloseModal = () => {
        setShowAwardModal(false);
        setEditingAward(null);
    };

    const handleDeleteAward = (award: Award) => {
        setAwardToDelete(award);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setAwardToDelete(null);
        setShowDeleteModal(false);
    };

    const handleConfirmDelete = () => {
        if (!awardToDelete) return;

        setIsDeleting(true);

        dispatch(actions.deleteAwardAction(awardToDelete._id, (res: any) => {
            if (res.success) {
            setAwards((prevAwards) => prevAwards.filter((a) => a._id !== awardToDelete._id));
            showSuccessToastMessage("Award deleted successfully!");
            //fetchAwards();
            } else {
            showErrorToastMessage(res.message || "Failed to delete award.");
            }

            setIsDeleting(false);
            handleCloseDeleteModal();
        }));
    };

    return (
        <>
            <MainCard>
                {/* Main Header */}
                <div className='title-container'>
                    <h2>Award Management</h2>
                </div>

                {/* Filter/Search bar */}
                <div className="mt-2">
                    <div className="mt-3 filter-wrapper">
                        <div className="emp-searchbar-wrapper">
                            <SearchBar
                                filter={searchQuery}
                                setFilter={setSearchQuery}
                                onSearchClicked={() => {}}
                                placeholderName="Search by Name or Eligibility"
                            />
                        </div>

                        <div className='right-actions'>
                            {/* Filter Button */}
                            <div className="emp-filter-container">
                                <Dropdown autoClose="outside">
                                    <Dropdown.Toggle variant="light" id="dropdown-filter-by-value">
                                        Filters
                                        {activeFiltersForDisplay.length > 0 && (
                                            <Badge bg="primary" pill className="ms-2">{activeFiltersForDisplay.length}</Badge>
                                        )}
                                    </Dropdown.Toggle>
                                    
                                    <Dropdown.Menu style={{ maxHeight: '300px', overflowY: 'auto', width: '250px' }}>
                                        {activeFiltersForDisplay.length > 0 && (
                                            <>
                                                <Dropdown.Item 
                                                    onClick={() => setFilters({})}
                                                > 
                                                    <span style={{ color: 'red' }}>Clear All Filters</span>
                                                </Dropdown.Item>
                                                <Dropdown.Divider />
                                            </>
                                        )}
                                        {Object.keys(filterOptions).map(field => (
                                            filterOptions[field].length > 0 && (
                                                <React.Fragment key={field}>
                                                    <Dropdown.Header>{field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</Dropdown.Header>
                                                    {filterOptions[field].map(option => (
                                                        <Dropdown.Item key={option} onClick={() => handleFilterChange(field, option)}>
                                                            <Form.Check type="checkbox" label={option} checked={(filters[field] || []).includes(option)} readOnly />
                                                        </Dropdown.Item>
                                                    ))}
                                                </React.Fragment>
                                            )
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>

                            {/* Add Award Button Row */}
                            <div>
                                {isHR && (
                                    <div className='m-4'>
                                        <button
                                            type="button"
                                            className="add-award-btn"
                                            onClick={() => handleOpenAddModal()}
                                        >
                                            Add New Award +
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                        <FilterValues 
                            filterValueArray={activeFiltersForDisplay} 
                            onFilterChange={removeFilter}
                        />
                        
                    </div> 
                </div>
                
                {/* Table Section */}
                {loading ? (
                    <div className="text-center p-5">
                        <span>Loading Awards...</span>
                    </div>
                ) : (
                    <Table
                     className='table-golden'
                     striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Frequency</th>
                                <th>Nomination Window</th>
                                <th>Window Status</th>
                                <th>Eligibility Criteria</th>
                                {isHR && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {processedAwards.length > 0 ? (
                                processedAwards.map((award: Award) => {
                                    const status = award.nominationWindowStatus?.status || 'N/A';
                                    const label = award.nominationWindowStatus?.label || 'N/A';
                                    
                                    let badgeBg = "secondary";
                                    switch (status) {
                                        case "Open": badgeBg = "success"; break;
                                        case "Closed": badgeBg = "danger"; break;
                                        case "Upcoming": badgeBg = "warning"; break;
                                        case "Always Open": badgeBg = "info"; break;
                                    }

                                    return (
                                        <tr key={award._id}>
                                            <td><b>{award.awardCategory || 'N/A'}</b></td>
                                            <td>{award.definition || 'N/A'}</td>
                                            <td><b><i>{award.nominationWindow?.frequency ?? award?.frequency ?? 'N/A'}</i></b></td>
                                            <td>{label}</td>
                                            <td className="text-center">
                                                <Badge className="award-badge" bg={badgeBg}>{status}</Badge>
                                            </td>
                                            <td>{getEligibilityText(award.eligibility)}</td>
                                            {isHR && (
                                                <td>
                                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}>
                                                        {/* Edit Button */}
                                                        <OverlayTrigger
                                                            placement="top"
                                                            delay={{ show: 250, hide: 400 }}
                                                            overlay={
                                                                <Tooltip id={`tooltip-edit-${award._id}`}>
                                                                    Edit Award
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <button 
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => handleOpenEditModal(award)}
                                                            >
                                                                <RiEditLine size={16} />
                                                            </button>
                                                        </OverlayTrigger>
                                                        {/* Delete Button */}
                                                        <OverlayTrigger 
                                                            placement="top" 
                                                            overlay={<Tooltip>Delete Award</Tooltip>}
                                                        >
                                                            <button className="btn btn-sm btn-outline-danger ms-2" onClick={() => handleDeleteAward(award)}>
                                                                <RiDeleteBinLine size={16} />
                                                            </button>
                                                        </OverlayTrigger>
                                                    </div>
                                                    
                                                </td>
                                            )}    
                                        </tr>
                                        )
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="text-center">No awards match your criteria.</td>
                                    </tr>
                            )}
                        </tbody>
                    </Table>
                )}

                <AwardModal
                    show={showAwardModal}
                    onHide={handleCloseModal}
                    onSuccess={fetchAwards}
                    awardToEdit={editingAward} 
                />

                <DeleteAwardModal
                    show={showDeleteModal}
                    onHide={handleCloseDeleteModal}
                    onConfirm={handleConfirmDelete}
                    title="Confirm Deletion"
                    body={
                        <>
                            Are you sure you want to delete the award: <strong>{awardToDelete?.awardCategory}</strong>? 
                            This action cannot be undone.
                        </>
                    }
                    confirmButtonText="Delete"
                    confirmButtonVariant="danger"
                    isConfirming={isDeleting}
                />
                
            </MainCard>
        </>
    );
};

export default Awards;